export interface PhotoFileValue {
	image: string;
	mime: string;
}

export type PhotoFileErrorCode = "read-failed" | "too-large" | "unsupported-type";

export type ReadPhotoFileResult =
	| { error: string; errorCode: PhotoFileErrorCode; value?: undefined }
	| { error?: undefined; errorCode?: undefined; value: PhotoFileValue };

export const MAX_PHOTO_FILE_BYTES = 5 * 1024 * 1024;
export const ACCEPTED_PHOTO_MIME_TYPES = ["image/jpeg", "image/png", "image/webp"];

export const MAX_PHOTO_FILE_MB = Math.round(MAX_PHOTO_FILE_BYTES / (1024 * 1024));
const UNSUPPORTED_PHOTO_TYPE_ERROR = "Please choose a JPEG, PNG, or WEBP image";
const UNSUPPORTED_PHOTO_TYPE_RESULT = { error: UNSUPPORTED_PHOTO_TYPE_ERROR, errorCode: "unsupported-type" } as const;

export const splitPhotoDataUrl = (dataUrl: string): PhotoFileValue | null => {
	const match = /^data:([^;,]+);base64,(.+)$/s.exec(dataUrl);
	if (!match) return null;
	const [, mime, image] = match;
	if (!ACCEPTED_PHOTO_MIME_TYPES.includes(mime) || !image) return null;
	return { image, mime };
};

const readDataUrl = (file: File): Promise<string> =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(reader.result as string);
		reader.onerror = () => reject(reader.error ?? new Error("Failed to read file"));
		reader.readAsDataURL(file);
	});

const readPhotoFile = async (file: File): Promise<ReadPhotoFileResult> => {
	if (!ACCEPTED_PHOTO_MIME_TYPES.includes(file.type)) return UNSUPPORTED_PHOTO_TYPE_RESULT;
	if (file.size > MAX_PHOTO_FILE_BYTES) {
		return { error: `Image must be smaller than ${MAX_PHOTO_FILE_MB} MB`, errorCode: "too-large" };
	}

	let dataUrl: string;
	try {
		dataUrl = await readDataUrl(file);
	} catch {
		return { error: "Could not read the selected file", errorCode: "read-failed" };
	}

	const value = splitPhotoDataUrl(dataUrl);
	if (!value) return UNSUPPORTED_PHOTO_TYPE_RESULT;
	return { value };
};

export default readPhotoFile;
